/**
 * WebVitals component that reports Core Web Vitals through the logging
 * pipeline.
 *
 * Uses the web-vitals library to capture Core Web Vitals and reports them
 * through a `Logger` passed in as a prop.
 *
 * Mount this component once, near the root of the tree — see
 * apps/retells/src/logging/LoggerProvider.tsx.
 */

import { useEffect, useRef } from "react";
import type { Metric } from "web-vitals";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
import type { Logger } from "./logger";

export interface WebVitalsProps {
  logger: Logger;
}

/**
 * WebVitals reporter component.
 *
 * Reports the following metrics:
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - INP (Interaction to Next Paint)
 * - LCP (Largest Contentful Paint)
 * - TTFB (Time to First Byte)
 *
 * @example
 * ```tsx
 * <WebVitals logger={log} />
 * ```
 */
export function WebVitals({ logger }: WebVitalsProps) {
  const logRef = useRef<Logger>(logger);

  // Guards the registration effect below against Strict Mode's dev-only
  // mount -> cleanup -> mount replay. web-vitals exposes no unsubscribe for
  // onCLS/onFCP/onINP/onLCP/onTTFB, so a second run would add a second live
  // reporter per metric that can never be torn down, and every value would
  // log twice. A ref rather than a module-scope flag (contrast
  // apps/retells/src/install/useInstallOffer.ts's loadRecordedThisDocument):
  // this component lives in a shared package, so nothing stops a consumer
  // from mounting more than one instance, each wired to its own logger — a
  // module-scope flag would silently starve every instance after the first,
  // where a ref only guards the replay of its own instance's effect and
  // survives that replay because React keeps refs across it.
  const hasRegistered = useRef(false);

  // Keep ref updated without triggering the registration effect
  useEffect(() => {
    logRef.current = logger;
  }, [logger]);

  // Register metrics handlers once on mount
  useEffect(() => {
    if (hasRegistered.current) return;
    hasRegistered.current = true;

    const report = (metric: Metric) => {
      logRef.current.event("web.vitals", {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_id: metric.id,
        metric_rating: metric.rating,
      });
    };

    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  }, []);

  return null;
}
